<template>
    <div class="league-page">
        <page-hero
            :title="league ? league.title : ''"
            :eyebrow="league && league.leagueGroup ? league.leagueGroup.title : 'League'"
        >
            <!-- only the phases with matches; a new list starts again from its first option -->
            <match-type-choice :key="availableTypes.join()" :options="availableTypes.map(type => typeTitles[type])" @setType="setType" />
        </page-hero>
        <div class="cl-page">
            <v-tabs v-model="tab" class="cl-tabs mb-6" background-color="transparent" show-arrows>
                <v-tabs-slider class="custom-teal" />
                <v-tab v-for="(item, index) in items" :key="index">{{ item.name }}</v-tab>
            </v-tabs>
            <template v-if="tab === 0">
                <!-- the bracket made in the CMS; without one, the picture of the standings -->
                <template v-if="type === 'playoffs'">
                    <bracket-board v-if="bracketLoaded" :bracket="bracket" @match="openMatch" />
                </template>
                <league-standings v-else :ranking="ranking" />
            </template>
            <!-- every match of the league in the active season, not the programme of one team -->
            <schedule-league v-else-if="tab === 1" :matches="matches" />
            <league-stats v-else-if="tab === 2 && league && seasonId" :key="seasonId" :league="league" :season="seasonId" />
        </div>
    </div>
</template>

<script>
    import routeTab from '@/mixins/routeTab'
    import ScheduleLeague from '@/components/ScheduleMatchCenter'
    import LeagueStats from '@/components/LeagueStats'
    import leagueService from '@/services/league'
    import LeagueStandings from '@/components/LeagueStandings'
    import matchService from '@/services/match'
    import MatchTypeChoice from '@/components/MatchTypeChoice'
    import BracketBoard from '@/components/BracketBoard'
    import bracketService from '@/services/bracket'
    import PageHero from '@/components/PageHero'
    import seasonService from '@/services/season'
    import { availablePhases } from '@/utils/phases'

    export default {
        components: { ScheduleLeague, LeagueStandings, LeagueStats, MatchTypeChoice, BracketBoard, PageHero },
        mixins: [routeTab],
        data() {
            return {
                tab: null,
                items: [
                    {
                        name: 'Standings'
                    },
                    {
                        name: 'Match Center'
                    },
                    {
                        name: 'Statistics'
                    }
                ],
                ranking: [],
                page: 1,
                size: 1000,
                totalPages: 0,
                matches: null,
                // the matches of the latest request only
                matchesRequest: 0,
                bracket: null,
                bracketLoaded: false,
                type: 'regular_season',
                matchTypes: ['regular_season', 'playoffs'],
                typeTitles: { regular_season: 'Regular Season', playoffs: 'Play Offs' },
                // what the active season has to show (null: unknown, all the options)
                seasonPhases: null,
                league: null
            }
        },
        computed: {
            seasonId() {
                return this.$store.getters.getConfig?.activeSeasonId || null
            },
            // the phases of the league with matches (the playoffs also with a bracket); never none
            availableTypes() {
                const options = this.matchTypes.map(value => ({ value }))
                const types = availablePhases(options, this.seasonPhases, { id: Number(this.$route.params.id) }).map(option => option.value)
                return types.length ? types : this.matchTypes
            }
        },
        watch: {
            // the config comes after the page on a fresh load: everything again for the active season
            seasonId() {
                this.fetchSeasonPhases()
                this.getRanking(this.$route.params.id)
                this.fetchMatchesByLeague()
                this.fetchBracket()
            },
            availableTypes(types, previous) {
                if (types.join() !== previous.join()) {
                    this.type = types[0]
                }
            },
            '$route.params.id': {
                handler() {
                    this.getLeague(this.$route.params.id)
                    this.getRanking(this.$route.params.id)
                    this.fetchMatchesByLeague()
                    this.fetchBracket()
                }
            },
            type: {
                handler() {
                    this.getLeague(this.$route.params.id)
                    this.getRanking(this.$route.params.id)
                    this.fetchMatchesByLeague()
                    this.fetchBracket()
                }
            }
        },
        created() {
            this.fetchSeasonPhases()
            this.getLeague(this.$route.params.id)
            this.getRanking(this.$route.params.id)
            this.fetchMatchesByLeague()
            this.fetchBracket()
        },
        methods: {
            setType(option) {
                this.type = this.availableTypes[option]
            },
            fetchSeasonPhases() {
                const seasonId = this.$store.getters.getConfig.activeSeasonId
                if (!seasonId) return
                seasonService.getPhases(seasonId).then(resp => {
                    this.seasonPhases = resp.data || null
                }).catch(() => {})
            },
            getLeague(leagueId) {
                leagueService.getLeague(leagueId, this.type).then(resp => {
                    this.league = resp.data
                })
            },
            getRanking(leagueId) {
                leagueService.getRankingByLeague(leagueId, this.$store.getters.getConfig.activeSeasonId, this.type).then(resp => {
                    this.ranking = resp.data
                })
            },
            // the playoffs bracket of the league in the active season (made in the CMS, or the picture of the standings)
            fetchBracket() {
                const seasonId = this.$store.getters.getConfig.activeSeasonId
                const leagueId = this.$route.params.id
                this.bracket = null
                this.bracketLoaded = false
                if (this.type !== 'playoffs') return
                if (!seasonId) {
                    this.bracketLoaded = true
                    return
                }
                const current = () => this.type === 'playoffs' && String(this.$route.params.id) === String(leagueId)
                bracketService.getBracket({ seasonId, leagueId }).then(resp => {
                    if (current()) {
                        this.bracket = resp.data || null
                    }
                }).catch(() => {}).finally(() => {
                    if (current()) {
                        this.bracketLoaded = true
                    }
                })
            },
            openMatch(matchId) {
                this.$router.push(`/Match/${matchId}`)
            },
            fetchMatchesByLeague() {
                const requestId = ++this.matchesRequest
                this.matches = null
                if (!this.seasonId) return
                const filters = { league: `{league: ${this.$route.params.id}, phase: ${this.type}, season: ${this.seasonId}, isCup: false}` }
                matchService.getMatches(1, 300, filters).then(resp => {
                    if (requestId === this.matchesRequest) this.matches = resp.data.content
                }).catch(() => {
                    if (requestId === this.matchesRequest) this.matches = []
                })
            }
        }
    }
</script>

<style scoped>
    .mobile-sticky-banner {
        position: sticky;
        top: 80px;
        z-index: 1;
    }

    .pc-sticky-banner {
        position: sticky;
        top: 60px;
        z-index: 1;
    }

</style>
